import {
  FormCheckboxField,
  FormNumberField,
  FormRadioGroup,
  FormTextField,
} from '@mfw/forms';
import { isDefined, QuestionnaireAnswerType } from '@mfw/common';
import { RadioField, useIntl } from '@mfw/components';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';

import type { ApiQuestionnaireQuestion } from '../../schemas/questionnaires.schema';

import messages from './messages';

const QuestionAnswerControl = ({
  question,
}: {
  question: ApiQuestionnaireQuestion;
}) => {
  const { formatMessage } = useIntl();

  switch (question.answerType) {
    case QuestionnaireAnswerType.TEXT:
      return (
        <FormTextField
          data-testid="questionnaire-answer-text"
          name={question.id}
          multiline
          fullWidth
        />
      );
    case QuestionnaireAnswerType.NUMBER:
      return (
        <FormNumberField
          data-testid="questionnaire-answer-number"
          max={question.numberMax ?? undefined}
          min={question.numberMin ?? undefined}
          name={question.id}
        />
      );
    case QuestionnaireAnswerType.SCALE:
      return (
        <FormNumberField
          data-testid="questionnaire-answer-scale"
          endAdornment={question.unitLabel ?? undefined}
          max={question.scaleMax ?? undefined}
          min={question.scaleMin ?? undefined}
          step={question.scaleStep ?? undefined}
          name={question.id}
        />
      );
    case QuestionnaireAnswerType.YES_NO:
      return (
        <FormRadioGroup
          data-testid="questionnaire-answer-yes-no"
          name={question.id}
        >
          <RadioField label={formatMessage(messages.yes)} value="true" />
          <RadioField label={formatMessage(messages.no)} value="false" />
        </FormRadioGroup>
      );
    case QuestionnaireAnswerType.SINGLE_SELECT:
      return (
        <FormRadioGroup
          data-testid="questionnaire-answer-single-select"
          name={question.id}
        >
          {(question.options ?? []).map((option) => (
            <RadioField
              label={option.optionText}
              value={option.id}
              key={option.id}
            />
          ))}
        </FormRadioGroup>
      );
    case QuestionnaireAnswerType.MULTISELECT:
      return (
        <Box flexDirection="column" display="flex">
          {(question.options ?? []).map((option) => (
            <FormCheckboxField
              data-testid="questionnaire-answer-multiselect"
              label={option.optionText}
              name={`${question.id}.${option.id}`}
              key={option.id}
            />
          ))}
        </Box>
      );
    default:
      return null;
  }
};

export const QuestionField = ({
  question,
  index,
}: {
  question: ApiQuestionnaireQuestion;
  index?: number;
}) => {
  const { formatMessage } = useIntl();

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
        bgcolor: 'background.paper',
      }}
    >
      <Box alignItems="center" display="flex" gap={1}>
        {index !== undefined && (
          <Box
            sx={{
              bgcolor: 'action.selected',
              borderRadius: 1,
              fontSize: '0.75rem',
              fontWeight: 700,
              px: 1,
              py: 0.5,
              whiteSpace: 'nowrap',
            }}
          >
            {formatMessage(messages.questionLabel, { number: index + 1 })}
          </Box>
        )}
        <Typography variant="subtitle1" fontWeight={600}>
          {question.questionText}
          {question.isRequired ? ' *' : ''}
          {question.answerType === QuestionnaireAnswerType.SCALE &&
          isDefined(question.scaleMin) &&
          isDefined(question.scaleMax)
            ? ` ${formatMessage(messages.scaleRange, {
                min: question.scaleMin,
                max: question.scaleMax,
              })}`
            : ''}
        </Typography>
      </Box>

      {question.description && (
        <Typography color="text.secondary" variant="body2" mt={0.5}>
          {question.description}
        </Typography>
      )}

      <Divider sx={{ my: 2 }} />
      <Box>
        <QuestionAnswerControl question={question} />
      </Box>
    </Box>
  );
};
